/* Checking in — the phone says where you are, the server says whether that
 * is church.
 *
 * WHO, THEN WHERE. The household comes first, every name ticked: a family
 * arrives together, and the usual change is to untick the one who stayed
 * home. Only then is the location asked for, so the browser's permission
 * prompt appears because somebody pressed "Check in", never on its own.
 *
 * ONE RETRY FOR A ROUGH FIX. The first reading indoors is often a few
 * hundred metres out; a second, a moment later, is usually close. Whatever
 * comes back is sent with its accuracy and the server decides.
 */
import { useEffect, useState } from 'react'
import { AlertCircle, Check, Loader2, MapPin } from 'lucide-react'
import { ApiError } from '@/api/client'
import { useCheckIn, useMe, useUndoCheckIn } from '@/api/queries'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { cn } from '@/lib/utils'
import type { AttendanceToday, CheckedIn } from '@/types/api'
import { clock, relationLabel } from './parts'

const ROUGH_M = 100

class LocationError extends Error {}

function position(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15_000,
      maximumAge: 0,
    }),
  )
}

function explain(err: GeolocationPositionError): string {
  if (err.code === err.PERMISSION_DENIED) {
    return (
      'Your browser is not sharing your location with us. Allow it from the ' +
      'icon beside the web address (or in your phone’s settings), then try ' +
      'again — or ask someone at the welcome desk to mark you present.'
    )
  }
  return (
    'We could not tell where you are just now. Try again in a moment, ' +
    'or ask someone at the welcome desk to mark you present.'
  )
}

/** Where this device is, as well as it can say in two tries. */
export async function locate(): Promise<GeolocationPosition> {
  if (!('geolocation' in navigator)) {
    throw new LocationError(
      'This browser cannot share your location. Ask someone at the welcome desk to mark you present.',
    )
  }
  let first: GeolocationPosition
  try {
    first = await position()
  } catch (err) {
    throw new LocationError(explain(err as GeolocationPositionError))
  }
  if (first.coords.accuracy <= ROUGH_M) return first
  try {
    const second = await position()
    return second.coords.accuracy < first.coords.accuracy ? second : first
  } catch {
    return first
  }
}

/** "You and 3 family members checked in at 9:12 am" */
export function checkedInSentence(c: CheckedIn, meId: string | undefined): string {
  const mine = c.people.some((p) => p.id === meId)
  const others = c.people.filter((p) => p.id !== meId)
  const who = mine
    ? others.length
      ? `You and ${others.length} family ${others.length === 1 ? 'member' : 'members'}`
      : 'You'
    : others.map((p) => p.name.split(' ')[0]).join(', ')
  return `${who} checked in at ${clock(c.at)}`
}

/** The green card once somebody is in, with Undo while it is allowed. */
export function CheckedInCard({
  churchId,
  occurrenceId,
  checkedIn,
  onUndone,
}: {
  churchId: string
  occurrenceId: string
  checkedIn: CheckedIn
  /** Told once the undo has gone through, for a sheet holding its own copy. */
  onUndone?: () => void
}) {
  const { data: me } = useMe()
  const undo = useUndoCheckIn(churchId)
  return (
    <div className="flex flex-wrap items-center gap-s3 rounded-md bg-success-soft px-s4 py-s3 text-success-ink">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-pill bg-success text-white">
        <Check className="h-5 w-5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-base font-semibold">{checkedInSentence(checkedIn, me?.id)}</p>
        {checkedIn.slot_label ? (
          <p className="text-sm">{checkedIn.slot_label}</p>
        ) : null}
      </div>
      {checkedIn.can_undo ? (
        <Button
          size="sm"
          variant="ghost"
          className="text-success-ink"
          loading={undo.isPending}
          onClick={() => undo.mutate(occurrenceId, { onSuccess: () => onUndone?.() })}
        >
          Undo
        </Button>
      ) : null}
      {undo.isError ? (
        <p role="alert" className="w-full text-sm text-danger">
          {(undo.error as Error).message}
        </p>
      ) : null}
    </div>
  )
}

/** The sheet: who is here, one button, then the answer. */
export function CheckInDialog({
  churchId,
  occurrenceId,
  today,
  onClose,
}: {
  churchId: string
  occurrenceId: string
  today: AttendanceToday
  onClose: () => void
}) {
  const checkIn = useCheckIn(churchId)
  const service = today.services.find((s) => s.occurrence_id === occurrenceId)
  const household = today.household
  const [ticked, setTicked] = useState<Set<string>>(
    () => new Set(household.map((p) => p.id)),
  )
  const [phase, setPhase] = useState<'choose' | 'locating' | 'sending'>('choose')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<CheckedIn | null>(service?.checked_in ?? null)

  // Keyed on the ids, not the array: a refetch on focus hands back a new
  // array with the same people, and must not undo somebody's unticking.
  const ids = household.map((p) => p.id).join(',')
  useEffect(() => {
    setTicked(new Set(ids ? ids.split(',') : []))
  }, [ids])

  const count = household.length ? ticked.size : 1

  async function go() {
    setError(null)
    setPhase('locating')
    let pos: GeolocationPosition
    try {
      pos = await locate()
    } catch (err) {
      setError((err as Error).message)
      setPhase('choose')
      return
    }
    setPhase('sending')
    checkIn.mutate(
      {
        occurrenceId,
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy_m: Number.isFinite(pos.coords.accuracy) ? pos.coords.accuracy : null,
        is_mock: false,
        people: household.length ? [...ticked] : undefined,
      },
      {
        onSuccess: (c) => {
          setDone(c)
          setPhase('choose')
        },
        onError: (err) => {
          setError(
            err instanceof ApiError && err.message
              ? err.message
              : 'That did not go through. Try again, or ask someone at the welcome desk to mark you present.',
          )
          setPhase('choose')
        },
      },
    )
  }

  const title = service ? service.name : 'Check in'

  if (done) {
    return (
      <Modal open onOpenChange={(o) => !o && onClose()} title={title} width="440px">
        <div className="space-y-s4 p-s4">
          <CheckedInCard
            churchId={churchId}
            occurrenceId={occurrenceId}
            checkedIn={done}
            onUndone={() => setDone(null)}
          />
          <Button variant="primary" full onClick={onClose}>
            Done
          </Button>
        </div>
      </Modal>
    )
  }

  const busy = phase !== 'choose'

  return (
    <Modal
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={title}
      width="440px"
      footer={
        <Button
          variant="primary"
          size="lg"
          full
          onClick={go}
          loading={busy}
          disabled={count === 0}
        >
          {phase === 'locating' ? (
            'Finding where you are…'
          ) : (
            <>
              <MapPin className="h-4 w-4" aria-hidden />
              {count === 1 ? 'Check in' : `Check in ${count} people`}
            </>
          )}
        </Button>
      }
    >
      <div className="space-y-s3 p-s4">
        {service?.current_slot_label ? (
          <p className="text-base text-ink-2">{service.current_slot_label}</p>
        ) : null}

        {household.length > 1 ? (
          <>
            <p className="text-sm font-semibold text-ink-2">Who is here?</p>
            <ul className="divide-y divide-line-2 rounded border border-line-2">
              {household.map((p) => {
                const on = ticked.has(p.id)
                return (
                  <li key={p.id}>
                    <label className="flex cursor-pointer items-center gap-s3 px-s3 py-s3">
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={busy}
                        onChange={() => {
                          const next = new Set(ticked)
                          if (on) next.delete(p.id)
                          else next.add(p.id)
                          setTicked(next)
                        }}
                        className="h-5 w-5 accent-[var(--cn-brand)]"
                      />
                      <span className="min-w-0 flex-1 truncate text-base font-semibold">
                        {p.is_me ? `${p.name} (you)` : p.name}
                      </span>
                      {!p.is_me ? (
                        <span className="text-sm text-ink-2">{relationLabel(p.relation)}</span>
                      ) : null}
                    </label>
                  </li>
                )
              })}
            </ul>
          </>
        ) : (
          <p className="text-base text-ink-2">
            We will check your location to make sure you are at church.
          </p>
        )}

        {!today.location_set ? (
          <p className="text-sm text-ink-2">
            {today.church_name} has not set its location yet, so checking in by
            phone is not possible. Ask someone at the welcome desk to mark you present.
          </p>
        ) : null}

        {error ? (
          <p
            role="alert"
            className={cn(
              'flex items-start gap-s2 rounded-md bg-danger-soft px-s3 py-s2 text-base text-danger',
            )}
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            {error}
          </p>
        ) : null}

        {phase === 'sending' ? (
          <p className="flex items-center gap-s2 text-sm text-ink-2">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Checking you in…
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
